import { useMemo } from "react";
import { SxProps, Theme } from "@mui/material/styles";
import { UsersModel } from "../../../api";
import FilterableSelect from "../../basic/FilterableSelect";
import { useClioUsers } from "./useClioUsers";

type ValueMode = "id" | "name";

type ClioUserSelectProps = {
  value?: number | string;
  onChange: (value: number | string | undefined, user?: UsersModel | null) => void;
  valueMode?: ValueMode;
  label?: string;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
  includeAll?: boolean;
  includeNone?: boolean;
  allOptionLabel?: string;
  allOptionValue?: number | string;
  minWidth?: number;
  fullWidth?: boolean;
  required?: boolean;
  /** Exclude disabled Clio users from the list. */
  removeDisabled?: boolean;
  /** When true, options are not loaded internally — pass `users` instead. */
  users?: UsersModel[];
};

export default function ClioUserSelect({
  value,
  onChange,
  valueMode = "name",
  label = "Team Member",
  size = "small",
  sx,
  includeAll = true,
  includeNone = false,
  allOptionLabel,
  allOptionValue,
  minWidth,
  fullWidth = false,
  required = false,
  removeDisabled = false,
  users: usersProp,
}: ClioUserSelectProps) {
  const { users: loadedUsers } = useClioUsers(
    removeDisabled,
    usersProp == null
  );
  const users = usersProp ?? loadedUsers;

  const selectOptions = useMemo(
    () =>
      users
        .filter((user) =>
          valueMode === "id" ? user.id != null : Boolean(user.name?.trim())
        )
        .map((user) => ({
          value: valueMode === "id" ? user.id! : user.name!,
          label: user.name ?? String(user.id ?? ""),
        })),
    [users, valueMode]
  );

  const handleChange = (next: number | string | undefined) => {
    if (next === undefined) {
      onChange(undefined, null);
      return;
    }

    const user =
      valueMode === "id"
        ? users.find((u) => u.id === Number(next)) ?? null
        : users.find((u) => u.name === next) ?? null;

    onChange(next, user);
  };

  return (
    <FilterableSelect
      label={label}
      value={value}
      options={selectOptions}
      onChange={handleChange}
      size={size}
      sx={sx}
      includeAll={includeAll}
      includeNone={includeNone}
      allOptionLabel={allOptionLabel}
      allOptionValue={allOptionValue}
      minWidth={minWidth}
      fullWidth={fullWidth}
      required={required}
    />
  );
}
